/**
 * DataSourcesAvailability Component
 *
 * Shows which data sources are available for pattern selection.
 * Addresses UX-003: Users should see at a glance what patterns are available.
 *
 * Displays:
 * - CSV import data status (for FROM_CSV_IMPORT pattern)
 */

import { Alert, Group, Badge, Text, Anchor } from '@mantine/core';
import { IconInfoCircle, IconCheck, IconX } from '@tabler/icons-react';
import { Link } from 'react-router-dom';

interface DataSourcesAvailabilityProps {
  /** Whether CSV import data exists (any month) */
  hasHourlyData: boolean;
  /** Number of months with CSV import data */
  hourlyDataMonths?: number;
  /** Year for the data */
  year: number;
  /** Loading state */
  loading?: boolean;
}

export function DataSourcesAvailability({
  hasHourlyData,
  hourlyDataMonths = 0,
  year,
  loading = false,
}: DataSourcesAvailabilityProps) {
  if (loading) {
    return null;
  }

  // Show success state if CSV data is available
  if (hasHourlyData) {
    return (
      <Alert
        icon={<IconInfoCircle size={16} />}
        color="green"
        variant="light"
        mb="md"
        p="sm"
      >
        <Group gap="md" wrap="wrap">
          <Text size="sm" fw={500}>Dostępne źródła danych ({year}):</Text>
          <Badge
            color="green"
            variant="light"
            leftSection={<IconCheck size={12} />}
          >
            Import CSV ({hourlyDataMonths}/12 mies.)
          </Badge>
        </Group>
      </Alert>
    );
  }

  return (
    <Alert
      icon={<IconInfoCircle size={16} />}
      color="blue"
      variant="light"
      mb="md"
      p="sm"
    >
      <Group gap="md" wrap="wrap" align="center">
        <Text size="sm" fw={500}>Dostępne źródła danych ({year}):</Text>

        {/* CSV Import Status */}
        <Badge
          color="gray"
          variant="outline"
          leftSection={<IconX size={12} />}
        >
          Import CSV
        </Badge>
      </Group>

      {/* Help text for missing data */}
      <Text size="xs" c="dimmed" mt="xs">
        Aby użyć wzorca "Z importu CSV",{' '}
        <Anchor component={Link} to="/konto/import" size="xs">
          zaimportuj dane godzinowe z pliku CSV
        </Anchor>
        .
      </Text>
    </Alert>
  );
}
